import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, User, MapPin, Star } from "lucide-react";
import { ARTICLES } from "@/data/teamData";
import Footer from "@/components/Footer";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div data-testid="article-not-found" className="bg-[#050B14] text-white min-h-screen flex flex-col items-center justify-center px-5 text-center">
        <h1 className="font-display text-5xl mb-4">ARTIKEL TIDAK DITEMUKAN</h1>
        <p className="text-white/60 mb-8">Maaf, berita yang kamu cari tidak tersedia.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D62839] text-white px-6 py-3 text-sm font-bold tracking-wider transition-colors"
        >
          <ArrowLeft size={16} /> KEMBALI KE BERANDA
        </Link>
      </div>
    );
  }

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div data-testid="article-page" className="bg-[#050B14] text-white min-h-screen">
      {/* Hero cover */}
      <div className="relative h-[55vh] min-h-[360px] w-full overflow-hidden">
        <img src={article.cover} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/40 to-[#050B14]/60" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-3xl mx-auto w-full px-5 sm:px-8 pb-10">
            <Link
              to="/#berita"
              data-testid="article-back"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-wider text-white/80 hover:text-[#E63946] transition-colors mb-5"
            >
              <ArrowLeft size={14} /> SEMUA BERITA
            </Link>
            <span className="block w-fit bg-[#E63946] text-white text-[10px] font-bold tracking-wider px-3 py-1 mb-4">
              {article.category}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-5 mt-5 text-white/60 text-sm">
              <span className="inline-flex items-center gap-2">
                <Calendar size={14} /> {formatDate(article.date)}
              </span>
              <span className="inline-flex items-center gap-2">
                <User size={14} /> {article.author}
              </span>
              {article.location && (
                <span className="inline-flex items-center gap-2">
                  <MapPin size={14} /> {article.location}
                </span>
              )}
              {article.rating && (
                <span className="inline-flex items-center gap-1.5 text-white">
                  <Star size={14} className="text-[#FFD60A] fill-[#FFD60A]" /> {article.rating} / 5
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        {article.content.map((p, i) => (
          <p key={i} className="text-white/80 leading-relaxed text-lg mb-6">
            {p}
          </p>
        ))}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
          <div className="text-xs font-bold tracking-[0.3em] text-[#E63946] mb-6">BACA JUGA</div>
          <div className="grid sm:grid-cols-2 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="group flex flex-col bg-[#0A1128] border border-white/10 hover:border-[#E63946] transition-colors overflow-hidden"
              >
                <div className="h-40 overflow-hidden">
                  <img src={a.cover} alt={a.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <h3 className="font-display text-xl leading-tight group-hover:text-[#E63946] transition-colors">{a.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
